import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { MetricPeriod } from '@/lib/utils'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'

const TAG = '[useMetricWeeks]'

/**
 * Интерфейс для данных из metric_weeks
 */
export interface MetricWeek {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
}

/**
 * Вычисляет недели на основе дат периода (fallback если metric_weeks не заполнена).
 *
 * metric_weeks — справочная таблица, которая заполняется вручную или скриптом.
 * Если строки для нужного периода отсутствуют (пустая таблица / период ещё не разбит),
 * этот fallback нарезает период на 7-дневные блоки от period_start.
 */
function computeWeeksFromPeriod(period: MetricPeriod, today: string): { current: MetricWeek | null; prev: MetricWeek | null } {
  const periodStart = new Date(period.period_start + 'T00:00:00')
  const todayDate = new Date(today + 'T00:00:00')
  
  const daysSinceStart = Math.floor((todayDate.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24))
  
  console.log(TAG, '🔧 computeWeeksFromPeriod:', {
    periodId: period.period_id,
    periodStart: period.period_start,
    periodEnd: period.period_end,
    today,
    daysSinceStart
  })

  if (daysSinceStart < 0) {
    console.log(TAG, '🔧 today раньше period_start → нет текущей недели')
    return { current: null, prev: null }
  }
  
  const weekNumber = Math.floor(daysSinceStart / 7) + 1
  
  const weekStartDate = new Date(periodStart)
  weekStartDate.setDate(weekStartDate.getDate() + (weekNumber - 1) * 7)
  
  const weekEndDate = new Date(weekStartDate)
  weekEndDate.setDate(weekEndDate.getDate() + 6)
  
  const periodEndDate = new Date(period.period_end + 'T00:00:00')
  if (weekEndDate > periodEndDate) {
    weekEndDate.setTime(periodEndDate.getTime())
  }
  
  // ВАЖНО: используем локальные getFullYear/getMonth/getDate, а НЕ toISOString()
  // т.к. toISOString() возвращает UTC и в зонах UTC+N даёт предыдущий день
  const fmt = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const weekLen = Math.floor((weekEndDate.getTime() - weekStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
  
  const current: MetricWeek = {
    week_id: -weekNumber, // synthetic (отрицательный = вычислен, не из БД)
    period_id: period.period_id,
    week_number_in_period: weekNumber,
    week_start: fmt(weekStartDate),
    week_end: fmt(weekEndDate),
    week_len_days: weekLen
  }
  
  let prev: MetricWeek | null = null
  if (weekNumber > 1) {
    const prevStartDate = new Date(periodStart)
    prevStartDate.setDate(prevStartDate.getDate() + (weekNumber - 2) * 7)
    const prevEndDate = new Date(prevStartDate)
    prevEndDate.setDate(prevEndDate.getDate() + 6)
    
    prev = {
      week_id: -(weekNumber - 1),
      period_id: period.period_id,
      week_number_in_period: weekNumber - 1,
      week_start: fmt(prevStartDate),
      week_end: fmt(prevEndDate),
      week_len_days: 7
    }
  }
  
  console.log(TAG, '🔧 Computed result:', {
    currentWeek: current ? `#${current.week_number_in_period} [${current.week_start}..${current.week_end}] (${current.week_len_days}д)` : null,
    prevWeek: prev ? `#${prev.week_number_in_period} [${prev.week_start}..${prev.week_end}]` : null,
  })

  return { current, prev }
}

/**
 * Hook для загрузки данных о текущей и предыдущей неделе из metric_weeks.
 * Если metric_weeks не содержит данных, вычисляет недели из дат периода.
 */
export function useMetricWeeks(period: MetricPeriod | null, isCurrentPeriod: boolean) {
  const [currentWeek, setCurrentWeek] = useState<MetricWeek | null>(null)
  const [prevWeek, setPrevWeek] = useState<MetricWeek | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const loadWeeks = async () => {
      if (!period || !isCurrentPeriod) {
        console.log(TAG, '⏭️ Skip:', { hasPeriod: !!period, isCurrentPeriod })
        setLoading(false)
        setCurrentWeek(null)
        setPrevWeek(null)
        return
      }

      setLoading(true)
      const today = getLocalIsoDate()

      console.log(TAG, '📅 Загрузка недель:', {
        periodId: period.period_id,
        periodStart: period.period_start,
        periodEnd: period.period_end,
        today,
        isCurrentPeriod
      })

      try {
        // 1. Пробуем загрузить из metric_weeks (точный источник)
        const { data: currentWeekData, error: currentError } = await supabase
          .from('metric_weeks')
          .select('*')
          .eq('period_id', period.period_id)
          .lte('week_start', today)
          .gte('week_end', today)
          .maybeSingle<MetricWeek>()

        if (currentError) {
          console.error(TAG, '❌ Ошибка запроса metric_weeks:', currentError.message)
        }

        console.log(TAG, '📦 Результат metric_weeks запроса:', {
          found: !!currentWeekData,
          data: currentWeekData
            ? `week_id=${currentWeekData.week_id} #${currentWeekData.week_number_in_period} [${currentWeekData.week_start}..${currentWeekData.week_end}]`
            : 'NULL (таблица пуста или нет строки для today)'
        })

        if (currentWeekData) {
          // --- Данные есть в БД ---
          setCurrentWeek(currentWeekData)

          if (currentWeekData.week_number_in_period > 1) {
            const { data: prevWeekData, error: prevError } = await supabase
              .from('metric_weeks')
              .select('*')
              .eq('period_id', period.period_id)
              .eq('week_number_in_period', currentWeekData.week_number_in_period - 1)
              .maybeSingle<MetricWeek>()

            if (prevError) {
              console.error(TAG, '❌ Ошибка загрузки prev week:', prevError.message)
            }

            console.log(TAG, '📦 Prev week из БД:', {
              found: !!prevWeekData,
              data: prevWeekData
                ? `week_id=${prevWeekData.week_id} #${prevWeekData.week_number_in_period} [${prevWeekData.week_start}..${prevWeekData.week_end}]`
                : 'NULL'
            })
            setPrevWeek(prevWeekData || null)
          } else {
            console.log(TAG, 'ℹ️ Первая неделя периода, предыдущей нет')
            setPrevWeek(null)
          }
        } else {
          // --- Fallback: вычисляем из дат периода ---
          console.log(TAG, '⚠️ metric_weeks пуста для period_id=', period.period_id,
            '→ вычисляем недели из дат периода')
          const computed = computeWeeksFromPeriod(period, today)
          setCurrentWeek(computed.current)
          setPrevWeek(computed.prev)
        }
      } catch (error) {
        console.error(TAG, '💥 Exception:', error)
        // Fallback при ошибке
        const computed = computeWeeksFromPeriod(period, today)
        setCurrentWeek(computed.current)
        setPrevWeek(computed.prev)
      } finally {
        setLoading(false)
      }
    }

    loadWeeks()
  }, [period?.period_id, isCurrentPeriod])

  return { currentWeek, prevWeek, loading }
}
